import { useEffect, useMemo, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ContainerPage from "../../components/Containers/ContainerPage";
import TitlePage from "../../components/Texts/TitlePage";
import HealthSummaryCards from "./components/HealthSummaryCards";
import ClientsTable from "./components/ClientsTable";
import AnnotationsSidebar from "./components/AnnotationsSidebar";
import useClientsList from "../../hooks/useClientsList";
import { useInView } from "react-intersection-observer";
import {
  ADMIN_DASHBOARD_TITLE,
  ADMIN_DASHBOARD_SUBTITLE,
  ADMIN_DASHBOARD_SEARCH_PLACEHOLDER,
  ADMIN_DASHBOARD_FILTER_STATUS_PLACEHOLDER,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import TextField from "../../components/Form/TextField";
import Select from "../../components/Form/Select";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import { OWNER_DASHBOARD_FILTER_STATUS_OPTIONS } from "../../utils/constants";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import {
  startListenerAnnotationReadState,
  stopListenerAnnotationReadState,
  startGetOwners,
} from "../../actions/annotations";

const AdminDashboard = () => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const currentOwnerUid = useSelectorApp((state) => state.auth.user?.uid);

  // Use the custom hook for clients management - simplified return
  const {
    displayedClients,
    groupedClients,
    healthSummaryGroups,
    hasMoreItems,
    isLoadingMore,
    loadMoreClients,
    totalClients,
    isLoading,
    filterStatus,
    setFilterStatus,
    searchTerm,
    setSearchTerm,
  } = useClientsList();

  const [selectedAnnotationTarget, setSelectedAnnotationTarget] =
    useState(null);
  const [annotationReadStateByScope, setAnnotationReadStateByScope] = useState(
    {}
  );
  const [ownersByID, setOwnersByID] = useState({});
  const annotationReadStateListenerRef = useRef(null);

  const onSelectAnnotationTarget = (target) => {
    setSelectedAnnotationTarget(target);
  };

  // Local state for search input with debounce
  const [localSearchTerm, setLocalSearchTerm] = useState("");
  const debouncedSearchRef = useRef(null);
  const lastSentSearchRef = useRef("");
  const filterStatusRef = useRef(filterStatus);
  filterStatusRef.current = filterStatus;

  // React Hook Form setup for common components
  const { control, setValue, watch } = useForm({
    defaultValues: {
      searchTerm: localSearchTerm,
      filterStatus:
        filterStatus === null ? "" : filterStatus ? "active" : "inactive",
    },
  });

  // Refs para evitar loops en sincronización
  const isInternalUpdateRef = useRef(false);

  // Sync external searchTerm → react-hook-form (when cleared externally)
  useEffect(() => {
    // Only sync if searchTerm was cleared or changed externally (not from our debounced update)
    if (
      !isInternalUpdateRef.current &&
      searchTerm !== lastSentSearchRef.current &&
      searchTerm !== localSearchTerm
    ) {
      setLocalSearchTerm(searchTerm);
      setValue("searchTerm", searchTerm);
      lastSentSearchRef.current = searchTerm;
    }
  }, [searchTerm, localSearchTerm, setValue]);

  // Sync external filterStatus → react-hook-form
  useEffect(() => {
    if (!isInternalUpdateRef.current) {
      const formFilterValue =
        filterStatus === null ? "" : filterStatus ? "active" : "inactive";
      const currentFormValue = watch("filterStatus");
      if (currentFormValue !== formFilterValue) {
        setValue("filterStatus", formFilterValue);
      }
    }
  }, [filterStatus, setValue, watch]);

  // Debounce implementation: update searchTerm in hook only after 350ms delay
  useEffect(() => {
    if (debouncedSearchRef.current) {
      clearTimeout(debouncedSearchRef.current);
    }

    debouncedSearchRef.current = setTimeout(() => {
      lastSentSearchRef.current = localSearchTerm;
      isInternalUpdateRef.current = true;
      const trimmed = String(localSearchTerm).trim();
      // Clear plan filter together with searchTerm so useClientsList does not
      // refetch with filterValue=null before the debounced search string arrives.
      if (trimmed.length > 0 && filterStatusRef.current !== null) {
        setFilterStatus(null);
        setValue("filterStatus", "");
      }
      setSearchTerm(localSearchTerm);
      isInternalUpdateRef.current = false;
    }, 350);

    return () => clearTimeout(debouncedSearchRef.current);
  }, [localSearchTerm, setSearchTerm, setFilterStatus, setValue]);

  // Handler para cambios en el campo de búsqueda
  const onSearchChange = (value) => {
    setLocalSearchTerm(value);
  };

  // Handler para cambios en el filtro de estado
  const onFilterStatusChange = (value) => {
    isInternalUpdateRef.current = true;

    // Limpiar búsqueda antes de cambiar el filtro de estado
    if (searchTerm.length > 0 || localSearchTerm.length > 0) {
      setSearchTerm("");
      setLocalSearchTerm("");
      setValue("searchTerm", "");
      lastSentSearchRef.current = "";
    }

    // Cambiar el filtro de estado
    if (value === "") {
      setFilterStatus(null);
    } else if (value === "active") {
      setFilterStatus(true);
    } else {
      setFilterStatus(false);
    }

    isInternalUpdateRef.current = false;
  };

  // Intersection Observer for infinite scroll
  const { ref: lastItemRef, inView } = useInView({
    triggerOnce: false,
    rootMargin: "50px 0px",
  });
  const loadMoreClientsRef = useRef(loadMoreClients);
  loadMoreClientsRef.current = loadMoreClients;

  // Handle infinite scroll — keep loadMoreClients out of deps so health-score
  // Redux updates do not re-trigger pagination while the sentinel stays in view.
  useEffect(() => {
    if (!inView || !hasMoreItems || isLoadingMore) return;
    loadMoreClientsRef.current();
  }, [inView, hasMoreItems, isLoadingMore]);

  // When the first page is fully hidden by client-side filters, the table renders
  // its empty state and the sentinel row never mounts, so keep paginating without it.
  const hasNoVisibleClients = (displayedClients?.length || 0) === 0;
  useEffect(() => {
    if (isLoading || isLoadingMore || !hasMoreItems || !hasNoVisibleClients) {
      return;
    }
    loadMoreClientsRef.current();
  }, [isLoading, isLoadingMore, hasMoreItems, hasNoVisibleClients]);

  // One summary unit per plan-active agency group (multi-advertiser uses average score).
  const healthSummary = useMemo(() => {
    let healthy = 0;
    let watchlist = 0;
    let atRisk = 0;

    (healthSummaryGroups || []).forEach((group) => {
      if (
        group.isHealthSummaryLoading ||
        group.healthSummaryScore === null ||
        group.healthSummaryScore === undefined
      ) {
        return;
      }

      const score = group.healthSummaryScore;
      if (score >= 80) {
        healthy++;
      } else if (score >= 60) {
        watchlist++;
      } else {
        atRisk++;
      }
    });

    return { healthy, watchlist, atRisk };
  }, [healthSummaryGroups]);

  const HIDE_HEALTH_SUMMARY_CARDS = true;

  useEffect(() => {
    let cancelled = false;

    dispatch(startGetOwners()).then((ownersData) => {
      if (!cancelled) {
        setOwnersByID(ownersData ?? {});
      }
    });

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useEffect(() => {
    if (!currentOwnerUid) {
      setAnnotationReadStateByScope({});
      return undefined;
    }

    let cancelled = false;

    dispatch(
      startListenerAnnotationReadState({
        ownerUID: currentOwnerUid,
        onUpdatedDataCallback: (data) => {
          if (!cancelled) {
            setAnnotationReadStateByScope(data ?? {});
          }
        },
        onReferenceAvailable: (ref) => {
          annotationReadStateListenerRef.current = ref;
        },
      })
    );

    return () => {
      cancelled = true;
      dispatch(
        stopListenerAnnotationReadState({
          listenerRef: annotationReadStateListenerRef.current,
        })
      );
      annotationReadStateListenerRef.current = null;
    };
  }, [currentOwnerUid, dispatch]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "flex-start",
        }}
      >
        <ContainerPage
          hasMarginBottom={false}
          sx={{
            flex: 1,
            minWidth: 0,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            overflowX: { xs: "auto", md: "hidden" },
          }}
        >
          {/* Health Summary Cards */}
          {!HIDE_HEALTH_SUMMARY_CARDS && (
            <Box
              sx={{
                width: { xs: "100%" },
                flexShrink: 0,
              }}
            >
              <HealthSummaryCards
                healthSummary={healthSummary}
                isLoading={false}
              />
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              width: "100%",
              px: 3,
              mt: HIDE_HEALTH_SUMMARY_CARDS ? 4 : 1,
              mb: 1.5,
              gap: 1,
              flexShrink: 0,
            }}
          >
            <TitlePage>{t(ADMIN_DASHBOARD_TITLE)}</TitlePage>
            <Typography
              variant="body1"
              sx={{ color: "#878383", pb: { xs: 1, md: 0 } }}
            >
              {t(ADMIN_DASHBOARD_SUBTITLE)}
            </Typography>
          </Box>

          <Box sx={{ width: "100%", px: 3 }}>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 1.5,
                flexWrap: "wrap",
                flexShrink: 0,
              }}
            >
              <TextField
                control={control}
                name="searchTerm"
                placeholder={t(ADMIN_DASHBOARD_SEARCH_PLACEHOLDER)}
                startIcon={
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ color: "#999999 !important", fontSize: 20 }}
                    />
                  </InputAdornment>
                }
                actionsChange={onSearchChange}
                sx={{
                  minWidth: 250,
                  "& .MuiInputBase-input": {
                    padding: "9px 12px",
                  },
                }}
                variant="filled"
                rules={{}}
                errors={{}}
              />
              <Select
                control={control}
                name="filterStatus"
                options={OWNER_DASHBOARD_FILTER_STATUS_OPTIONS}
                doTranslate={true}
                placeholder={t(ADMIN_DASHBOARD_FILTER_STATUS_PLACEHOLDER)}
                size="small"
                actionsChange={onFilterStatusChange}
                sx={{
                  minWidth: 180,
                  mb: 0.2,
                  "& .MuiInputBase-input": {
                    padding: "9px 12px",
                  },
                }}
                variant="filled"
                displayEmpty={true}
                showEmptyOption={false}
                rules={{}}
                errors={{}}
              />
            </Box>

            <ClientsTable
              isLoading={isLoading}
              displayedClients={displayedClients}
              groupedClients={groupedClients}
              hasMoreItems={hasMoreItems}
              isLoadingMore={isLoadingMore}
              lastItemRef={lastItemRef}
              totalClients={totalClients}
              onSelectAnnotationTarget={onSelectAnnotationTarget}
              annotationReadStateByScope={annotationReadStateByScope}
            />
          </Box>
        </ContainerPage>

        <AnnotationsSidebar
          key={
            selectedAnnotationTarget
              ? `${selectedAnnotationTarget.advertiserID}-${selectedAnnotationTarget.isAgency}`
              : "annotations-sidebar"
          }
          target={selectedAnnotationTarget}
          isVisible={Boolean(selectedAnnotationTarget)}
          onClose={() => setSelectedAnnotationTarget(null)}
          ownersByID={ownersByID}
        />
      </Box>
    </>
  );
};

export default AdminDashboard;
